import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getAuth } from "firebase/auth";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  LinearProgress,
  Stack,
  Avatar,
} from "@mui/material";

import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import AuditSidebar from "../components/AuditSidebar";
import useAuditProgress from "../hooks/useAuditProgress";

const getStatusChipProps = (status) => {
  if (status === "Completed") {
    return {
      label: "Completed",
      sx: { bgcolor: "#dcfce7", color: "#16a34a", fontWeight: 700 },
    };
  }

  if (status === "In Progress") {
    return {
      label: "In Progress",
      sx: { bgcolor: "#ffedd5", color: "#ea580c", fontWeight: 700 },
    };
  }

  return {
    label: "Not Started",
    sx: { bgcolor: "#f1f5f9", color: "#64748b", fontWeight: 700 },
  };
};

const getButtonLabel = (status) => {
  if (status === "Completed") return "Open Form";
  if (status === "In Progress") return "Continue";
  return "Start";
};

const ProgressOverview = () => {
  const navigate = useNavigate();
  const { auditId } = useParams();

  const auth = getAuth();

  const displayName =
    auth.currentUser?.displayName ||
    auth.currentUser?.email?.split("@")[0] ||
    "User";

  const firstLetter = displayName.charAt(0).toUpperCase();
  const displayAuditId = auditId || "AUD-2026-001";

  const { formsData, overallProgress } = useAuditProgress(auditId);

  const lastUpdated = new Date().toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        bgcolor: "#f8fafc",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <AuditSidebar displayAuditId={displayAuditId} formsData={formsData} />

      <Box
        sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}
      >
        <Box
          sx={{
            bgcolor: "#ffffff",
            borderBottom: "1px solid #e5e7eb",
            px: { xs: 2, md: 3, lg: 4 },
            height: 64,
            display: "flex",
            alignItems: "center",
            gap: 4,
            flexShrink: 0,
          }}
        >
          <Box>
            <Typography fontSize={13.5} color="#64748b" fontWeight={700}>
              Report ID
            </Typography>
            <Typography fontWeight={800} fontSize={12}>
              {displayAuditId}
            </Typography>
          </Box>

          <Box>
            <Typography fontSize={13.5} color="#64748b" fontWeight={700}>
              STATUS
            </Typography>
            <Chip
              label={
                overallProgress >= 100
                  ? "Completed"
                  : overallProgress > 0
                    ? "In Progress"
                    : "Not Started"
              }
              size="small"
              sx={{
                bgcolor:
                  overallProgress >= 100
                    ? "#dcfce7"
                    : overallProgress > 0
                      ? "#eff6ff"
                      : "#f1f5f9",
                color:
                  overallProgress >= 100
                    ? "#16a34a"
                    : overallProgress > 0
                      ? "#2563eb"
                      : "#64748b",
                fontWeight: 700,
                fontSize: 12,
                height: 22,
                borderRadius: 99,
              }}
            />
          </Box>

          <Box>
            <Typography fontSize={13.5} color="#64748b" fontWeight={700}>
              COMPLETION
            </Typography>
            <Typography fontWeight={800} fontSize={12} color="#0ea5e9">
              {overallProgress}% Completed
            </Typography>
          </Box>

          <Box
            sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 2.5 }}
          >
            <Avatar
              sx={{
                width: 30,
                height: 30,
                bgcolor: "#dbeafe",
                color: "#2563eb",
                fontWeight: 800,
                fontSize: 13,
              }}
            >
              {firstLetter}
            </Avatar>

            <Box>
              <Typography fontSize={13.5} color="#64748b" fontWeight={700}>
                LAST UPDATED
              </Typography>
              <Typography fontWeight={700} fontSize={12}>
                {lastUpdated}
              </Typography>
            </Box>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                px: 1.25,
                py: 0.6,
                border: "1px solid #e2e8f0",
                borderRadius: 2,
                bgcolor: "#fff",
              }}
            >
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  bgcolor: "#2563eb",
                  fontWeight: 700,
                  fontSize: 12,
                }}
              >
                {firstLetter}
              </Avatar>

              <Typography fontSize={13.5} fontWeight={700}>
                {displayName}
              </Typography>
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            p: 3,
          }}
        >
          <Box sx={{ width: "100%" }}>
            <Card
              sx={{
                borderRadius: 2.5,
                border: "1px solid #e2e8f0",
                boxShadow: "0 4px 18px rgba(15,23,42,0.06)",
                mb: 2.5,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography fontWeight={800} fontSize={18} sx={{ mb: 2 }}>
                  Overall Audit Progress
                </Typography>

                <Stack direction="row" spacing={3} alignItems="center">
                  <Box sx={{ flex: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={overallProgress}
                      sx={{
                        height: 8,
                        borderRadius: 99,
                        bgcolor: "#e2e8f0",
                        mb: 1.5,
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 99,
                          background:
                            "linear-gradient(90deg, #2563eb, #0ea5e9)",
                        },
                      }}
                    />

                    <Typography fontSize={13} color="#64748b">
                      You're making good progress. Keep completing the remaining
                      steps.
                    </Typography>
                  </Box>

                  <Typography fontWeight={900} fontSize={36} color="#0ea5e9">
                    {overallProgress}%
                  </Typography>
                </Stack>
              </CardContent>
            </Card>

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Typography fontWeight={800} fontSize={18}>
                Audit Steps (1–6)
              </Typography>
            </Stack>

            <Grid container spacing={2}>
              {formsData.map((form, index) => {
                const chipProps = getStatusChipProps(form.status);
                const hasVisitedSummary =
                  sessionStorage.getItem(`summaryVisited_${auditId}`) ===
                  "true";

const prevProgress = formsData[index - 1]?.progress || 0;

let isLocked = false;

if (hasVisitedSummary) {
  isLocked = false;
} else if (index === 0) {
  isLocked = false;
} else {
  isLocked = prevProgress < 10;
}

                return (
                  <Grid item xs={12} sm={6} lg={4} key={form.key}>
                    <Card
                      onClick={() => {
                        if (!isLocked) navigate(`/${form.route}/${auditId}`);
                      }}
                      sx={{
                        height: "100%",
                        borderRadius: 2.5,
                        border: "1px solid #e2e8f0",
                        boxShadow: "0 4px 18px rgba(15,23,42,0.06)",
                        cursor: isLocked ? "not-allowed" : "pointer",
                        opacity: isLocked ? 0.55 : 1,
                        transition: "0.2s",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 6px 24px rgba(15,23,42,0.10)",
                        },
                      }}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="flex-start"
                          sx={{ mb: 2 }}
                        >
                          <Box
                            sx={{
                              bgcolor: form.bg,
                              color: form.color,
                              width: 40,
                              height: 40,
                              borderRadius: 2,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <Typography fontSize={18} fontWeight={900}>
                              {form.letter}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              fontSize={11}
                              fontWeight={800}
                              color={form.color}
                              sx={{ mb: 0.3 }}
                            >
                              {form.step}
                            </Typography>

                            <Typography
                              fontWeight={800}
                              fontSize={14.5}
                              sx={{ mb: 0.5 }}
                            >
                              {form.title}
                            </Typography>

                            <Chip
                              size="small"
                              {...chipProps}
                              sx={{ ...chipProps.sx, fontSize: 11, height: 20 }}
                            />
                          </Box>
                        </Stack>

                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ mb: 1 }}
                        >
                          <LinearProgress
                            variant="determinate"
                            value={form.progress}
                            sx={{
                              flex: 1,
                              height: 6,
                              borderRadius: 99,
                              bgcolor: "#e2e8f0",
                              "& .MuiLinearProgress-bar": {
                                bgcolor: form.color,
                                borderRadius: 99,
                              },
                            }}
                          />

                          <Typography fontWeight={800} fontSize={12}>
                            {form.progress}%
                          </Typography>
                        </Stack>

                        <Typography
                          color="#64748b"
                          fontSize={11.5}
                          sx={{ mb: 1.5 }}
                        >
                          Last updated: {form.updated}
                        </Typography>

                        <Button
                          fullWidth
                          variant="outlined"
                          disabled={isLocked}
                          endIcon={
                            <ArrowForwardIosIcon
                              sx={{ fontSize: "11px !important" }}
                            />
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isLocked)
                              navigate(`/${form.route}/${auditId}`);
                          }}
                          sx={{
                            borderRadius: 1.5,
                            textTransform: "none",
                            fontWeight: 700,
                            color: "#2563eb",
                            borderColor: "#dbeafe",
                            py: 0.75,
                            height: 36,
                            fontSize: 13,
                            "&:hover": {
                              bgcolor: "#eff6ff",
                              borderColor: "#93c5fd",
                            },
                          }}
                        >
                          {isLocked ? "Locked" : getButtonLabel(form.status)}
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProgressOverview;
